import React, { useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
} from "@mui/material";

import { ThunkDispatch } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";

import { RootState } from "../../store/Store";
import {
  FetchDataFailureAction,
  FetchDataSuccessAction,
  fetchData,
} from "../../store/actions";

import { listAttendanceRecords } from "../../Service/Apis";

import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { formatDateConsistent, formatDateRange, STANDARD_DATE_FORMAT } from "../../utils/dateUtils";
import CustomNoRowsOverlay from "../../components/Data/NoRowsOverlayComponent";
import NothingToDisplayComponent from "../../components/Data/NothingToShowComponent";
import PageTitle from "../../components/Page Component/PageTitle";
import { createRoleUtils } from "../../utils/roleUtils";
import { useResponsiveConfig, getOptimizedDataGridProps } from "../../utils/responsiveConfig";
import OptimizedLoader from "../../components/Loading/OptimizedLoader";
import ErrorBoundary from "../../components/Error/ErrorBoundary";
import { useErrorHandler } from "../../hooks/useErrorHandler";

const ROLE_LABELS = {
  employee: "Employee",
  manager: "Manager",
  hrAdmin: "HR/Admin",
};

const STATUS_VALUES = {
  workFromHome: "Work From Home",
  leavePaid: "Leave - Paid Leave",
  leaveMarriage: "Leave - Marriage",
  leaveLwp: "Leave - Leave Without Pay",
};

const TABLE_LABELS = {
  srNo: "Sr. No.",
  sr: "Sr.",
  employeeName: "Employee Name",
  name: "Name",
  monthYear: "Month-Year",
  month: "Month",
  date: "Date",
  dates: "Dates",
  workingHours: "Working Hours",
  hours: "Hours",
  wfh: "WFH",
  leaves: "Leaves",
  regularised: "Regularised",
  regularisedShort: "Reg.",
};

const SUMMARY_LABELS = {
  period: "Period",
  hoursLastMondayShort: "Hours (Last Mon.)",
  hoursLastMondayLong: "Hours from Last Monday",
  tillToday: "Till today",
  wfhThisMonth: "WFH Days (This Month)",
  workFromHome: "Work From Home",
  regularizations: "Regularizations",
  totalWorkingHours: "Total Working Hours",
  teamTotal: "Team Total",
  wfhCount: "WFH Count",
  wfhDays: "Work From Home Days",
  totalLeaves: "Total Leaves",
  allLeaveTypes: "All Leave Types",
  allEmployees: "All Employees",
  regularisedDays: "Regularised Days",
  acrossRecords: "Across",
  viewLabel: "View:",
};

const DEFAULT_YES_NO = {
  YES: "Yes",
  NO: "No",
};

const BUTTON_LABELS = {
  clear: "Clear",
};

const DATE_PICKER_LABELS = {
  from: "From",
  to: "To",
};

interface WeekDates {
  week_start: string;
  week_end: string;
}

interface AttendanceRecord {
  attendance_id: string;
  user_id: string;
  employee_name: string;
  month: number;
  year: number;
  date: string;
  present_days: number;
  paid_leaves: number;
  sick_leaves: number;
  leave_without_pay: number;
  late_marks: number;
  overtime_hours: number;
  regularized_days: number;
  created_at: string;
  week_number: number;
  employee_code: string;
  week_dates: WeekDates;
  // New fields
  shift_policy?: string;
  hours_worked?: number;
  status?: string;
  regularised?: boolean;
}

interface AttendanceSummary {
  total_late_marks: number;
  total_overtime: number;
  total_regularized_days: number;
  avg_late_marks: number;
  avg_overtime: number;
  total_records: number;
  date_range: {
    start_date: string;
    end_date: string;
  };
  message: string;
  user_roles: string[];
  // Employee-specific
  hours_from_last_monday?: number;
  wfh_current_month?: number;
  // New fields for all roles (date-aware)
  total_working_hours?: number;
  wfh_count?: number;
  total_leaves?: number;
  regularised_days?: number;
  total_regularizations?: number;
}

interface AttendanceResponse {
  results: AttendanceRecord[];
  summary: AttendanceSummary;
}

const EmployeeAttendance = () => {
  // Helper function to get last 30 days (for manual date selection)
  const getLast30DaysRange = () => {
    const toDate = dayjs().startOf('day');
    const fromDate = toDate.subtract(29, 'days'); // 30 days total (including today)
    return { fromDate, toDate };
  };

  // State Hooks - Initialize with null to let backend determine the range
  const [fromDate, setFromDate] = React.useState<Dayjs | null>(null);
  const [toDate, setToDate] = React.useState<Dayjs | null>(null);
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);
  const userID = localStorage.getItem("id");
  const [attendanceData, setAttendanceData] =
    useState<AttendanceResponse | null>(null);
  const [forceUpdateKey, setForceUpdateKey] = useState(0);

  // Error handling hook
  const { error, isLoading: hookLoading, handleAsyncOperation, clearError } = useErrorHandler();

  // Redux Hooks
  const dispatch2: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  const isLoading = useSelector((state: any) => state.loading.inPageLoading) || hookLoading;
  const userRoles = useSelector((state: RootState) => state.auth.roles);
  const selectedRole = useSelector((state: RootState) => state.auth.selectedRole);

  // Role-based utilities (for potential future use)
  const roleUtils = createRoleUtils({ userRoles, selectedRole });

  // Responsive configuration
  const responsive = useResponsiveConfig();

  const rows =
    attendanceData?.results.map((record, index) => {
      return {
        id: record.attendance_id,
        ...record,
        index: index,
        sr_no: index + 1,
        month_year: dayjs(`${record.year}-${record.month}-01`).format(
          "MMM-YYYY"
        ),
        formatted_date: formatDateConsistent(record.date),
      };
    }) || [];

  const columns: GridColDef[] = useMemo(() => {
    const baseColumns: GridColDef[] = [
      {
        field: "sr_no",
        headerName: TABLE_LABELS.srNo,
        flex: 0.6,
        align: "center",
        headerAlign: "center",
        headerClassName: "header-background",
        renderHeader: () => (
          <strong className="custom-header" style={{ fontSize: responsive.isMobile ? 13 : 15 }}>
            {responsive.isMobile ? TABLE_LABELS.sr : TABLE_LABELS.srNo}
          </strong>
        ),
      },
      {
        field: "employee_name",
        headerName: TABLE_LABELS.employeeName,
        align: "center",
        flex: 1.5,
        headerAlign: "center",
        headerClassName: "header-background",
        renderHeader: () => (
          <strong className="custom-header" style={{ fontSize: responsive.isMobile ? 13 : 15 }}>
            {responsive.isMobile ? TABLE_LABELS.name : TABLE_LABELS.employeeName}
          </strong>
        ),
      },
      {
        field: "month_year",
        headerName: TABLE_LABELS.monthYear,
        align: "center",
        flex: 0.9,
        headerAlign: "center",
        headerClassName: "header-background",
        renderHeader: () => (
          <strong className="custom-header" style={{ fontSize: responsive.isMobile ? 13 : 15 }}>
            {responsive.isMobile ? TABLE_LABELS.month : TABLE_LABELS.monthYear}
          </strong>
        ),
      },
      {
        field: "formatted_date",
        headerName: TABLE_LABELS.date,
        align: "center",
        flex: 1.0,
        headerAlign: "center",
        headerClassName: "header-background",
        renderHeader: () => (
          <strong className="custom-header" style={{ fontSize: responsive.isMobile ? 13 : 15 }}>
            {responsive.isMobile ? TABLE_LABELS.dates : TABLE_LABELS.date}
          </strong>
        ),
      },
      {
        field: "hours_worked",
        headerName: TABLE_LABELS.workingHours,
        flex: 0.9,
        align: "center",
        headerAlign: "center",
        headerClassName: "header-background",
        renderHeader: () => (
          <strong className="custom-header" style={{ fontSize: responsive.isMobile ? 13 : 15 }}>
            {responsive.isMobile ? TABLE_LABELS.hours : TABLE_LABELS.workingHours}
          </strong>
        ),
        valueFormatter: (params) => {
          return params.value ? `${params.value.toFixed(2)}h` : "0.00h";
        },
      },
      {
        field: "work_from_home",
        headerName: TABLE_LABELS.wfh,
        flex: 0.7,
        align: "center",
        headerAlign: "center",
        headerClassName: "header-background",
        renderHeader: () => (
          <strong className="custom-header" style={{ fontSize: responsive.isMobile ? 13 : 15 }}>
            {TABLE_LABELS.wfh}
          </strong>
        ),
        valueGetter: (params) => {
          // Count WFH days based on status
          return params.row.status === STATUS_VALUES.workFromHome ? 1 : 0;
        },
        renderCell: (params) => (
          <Chip
            label={params.value === 1 ? DEFAULT_YES_NO.YES : DEFAULT_YES_NO.NO}
            color={params.value === 1 ? "info" : "default"}
            variant="outlined"
            size={responsive.isMobile ? "small" : "small"}
          />
        ),
      },
      {
        field: "leaves",
        headerName: TABLE_LABELS.leaves,
        flex: 0.7,
        align: "center",
        headerAlign: "center",
        headerClassName: "header-background",
        renderHeader: () => (
          <strong className="custom-header" style={{ fontSize: responsive.isMobile ? 13 : 15 }}>
            {TABLE_LABELS.leaves}
          </strong>
        ),
        valueGetter: (params) => {
          // Prioritize status-based leaves over individual leave fields
          const isLeaveStatus = params.row.status && 
            (params.row.status.includes(STATUS_VALUES.leavePaid) ||
             params.row.status.includes(STATUS_VALUES.leaveMarriage) ||
             params.row.status.includes(STATUS_VALUES.leaveLwp));
          
          if (isLeaveStatus) {
            return 1;
          }
          
          // Only use individual leave fields if no status-based leave
          const paidLeaves = params.row.paid_leaves || 0;
          const sickLeaves = params.row.sick_leaves || 0;
          const lwp = params.row.leave_without_pay || 0;
          
          return paidLeaves + sickLeaves + lwp;
        },
        valueFormatter: (params) => {
          return params.value.toFixed(1);
        },
        renderCell: (params) => {
          let label = 'No';
          if (params.value === 1.0) {
            label = 'Full day';
          } else if (params.value === 0.5) {
            label = 'Half day';
          }
          return (
            <Chip
              label={label}
              color={params.value > 0 ? "success" : "default"}
              variant="outlined"
              size={responsive.isMobile ? "small" : "small"}
            />
          );
        },
      },
      {
        field: "regularised_days",
        headerName: TABLE_LABELS.regularised,
        flex: 0.9,
        align: "center",
        headerAlign: "center",
        headerClassName: "header-background",
        renderHeader: () => (
          <strong className="custom-header" style={{ fontSize: responsive.isMobile ? 13 : 15 }}>
            {responsive.isMobile ? TABLE_LABELS.regularisedShort : TABLE_LABELS.regularised}
          </strong>
        ),
        valueGetter: (params) => {
          // Use the new regularised boolean field if available, otherwise fall back to regularized_days
          if (params.row.regularised !== undefined) {
            return params.row.regularised ? 1 : 0;
          }
          return params.row.regularized_days || 0;
        },
        renderCell: (params) => (
          <Chip
            label={params.value > 0 ? DEFAULT_YES_NO.YES : DEFAULT_YES_NO.NO}
            color={params.value > 0 ? "warning" : "default"}
            variant="outlined"
            size={responsive.isMobile ? "small" : "small"}
          />
        ),
      },
    ];

    return baseColumns;
  }, [responsive]);

  useEffect(() => {
    // API call to get the attendance records
    const fetchAttendance = async () => {
      await handleAsyncOperation(
        async () => {
          const response = await dispatch2(
            fetchData(
              `${
                listAttendanceRecords +
                 (userID ? `?user_id=${userID}` : "") + 
                (fromDate && toDate
                  ? `&date_from=${fromDate
                      .format("YYYY-MM-DD")
                      .toString()}&date_to=${toDate
                      .format("YYYY-MM-DD")
                      .toString()}`
                  : "")
              }`
            )
          );
          setAttendanceData(response as AttendanceResponse);
          
          // On initial load, set the date range from the response
          if (isInitialLoad && response?.summary?.date_range) {
            const { start_date, end_date } = response.summary.date_range;
            if (start_date && end_date) {
              setFromDate(dayjs(start_date));
              setToDate(dayjs(end_date));
            }
            setIsInitialLoad(false);
          }
          
          return response;
        },
        {
          onError: (error) => {
            console.error("Failed to fetch attendance data:", error);
          }
        }
      );
    };

    // Always fetch on initial load, or when dates are explicitly set
    if (isInitialLoad || (fromDate && toDate)) {
      fetchAttendance();
    }
  }, [fromDate, toDate, forceUpdateKey, isInitialLoad, handleAsyncOperation, dispatch2, userID]);

  // Loading Spinner Component with optimized loader
  if (isLoading) {
    return <OptimizedLoader type="skeleton" message="Loading attendance data..." />;
  }

  // Error Display Component
  if (error) {
    return (
      <Box sx={{ width: "100%", mt: 12, mb: 10 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            maxWidth: "1600px",
            margin: "0 auto",
            padding: "0 16px",
          }}
        >
          <Alert 
            severity="error" 
            sx={{ width: "100%" }}
            action={
              <Button color="inherit" size="small" onClick={clearError}>
                Retry
              </Button>
            }
          >
            <Typography variant="body1">
              Failed to load attendance data: {error}
            </Typography>
          </Alert>
        </Box>
      </Box>
    );
  }

  // Main Component with Error Boundary
  return (
    <ErrorBoundary>
      <Box sx={{ width: "100%", mt: 12, mb: 10 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            maxWidth: "1600px", // Increased from 1400px to accommodate more columns
            margin: "0 auto",
            padding: "0 16px",
          }}
        >
          <Box sx={{ width: "100%" }}>
            {/* Page Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Box>
                <PageTitle>Employee Attendance</PageTitle>
                <Typography 
                  variant="body2" 
                  color="textSecondary" 
                  sx={{ mt: 0.5 }}
                >
                  View attendance records
                </Typography>
              </Box>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <DatePicker
                    label={DATE_PICKER_LABELS.from}
                    format={STANDARD_DATE_FORMAT}
                    sx={{
                      ".MuiInputLabel-root": {
                        transform: "translate(14px, 8px) scale(1)",
                      },
                      ".MuiInputLabel-shrink": {
                        transform: "translate(14px, -6px) scale(0.75)",
                      },
                      ".MuiInputBase-root .MuiInputBase-input": {
                        height: "0.55rem",
                        width: "8rem",
                      },
                      mx: 1,
                    }}
                    value={fromDate}
                    onChange={(newValue) => setFromDate(newValue)}
                    disableFuture
                    slotProps={{
                      textField: {
                        placeholder: STANDARD_DATE_FORMAT,
                        inputProps: {
                          'aria-label': `From date in ${STANDARD_DATE_FORMAT} format`
                        }
                      }
                    }}
                  />
                  <DatePicker
                    label={DATE_PICKER_LABELS.to}
                    format={STANDARD_DATE_FORMAT}
                    sx={{
                      ".MuiInputLabel-root": {
                        transform: "translate(14px, 8px) scale(1)",
                      },
                      ".MuiInputLabel-shrink": {
                        transform: "translate(14px, -6px) scale(0.75)",
                      },
                      ".MuiInputBase-root .MuiInputBase-input": {
                        height: "0.55rem",
                        width: "8rem",
                      },
                      mx: 1,
                    }}
                    value={toDate}
                    onChange={(newValue) => setToDate(newValue)}
                    disableFuture
                    slotProps={{
                      textField: {
                        placeholder: STANDARD_DATE_FORMAT,
                        inputProps: {
                          'aria-label': `To date in ${STANDARD_DATE_FORMAT} format`
                        }
                      }
                    }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    disableElevation
                    onClick={() => {
                      // Reset to let backend determine the range (last 30 records)
                      setFromDate(null);
                      setToDate(null);
                      setIsInitialLoad(true);
                    }}
                  >
                    {BUTTON_LABELS.clear}
                  </Button>
                </Box>
              </LocalizationProvider>
            </Box>

            {/* Summary Cards */}
            {attendanceData?.summary && (
              <Grid container spacing={responsive.getCardSpacing()} sx={{ mb: 4 }}>
                {/* Period Card - Always shown */}
                <Grid item xs={12} sm={12} md={2.4}>
                  <Card sx={{ height: "100%", boxShadow: 2 }}>
                    <CardContent sx={{ p: responsive.isMobile ? 2 : 3 }}>
                      <Typography
                        variant={responsive.isMobile ? "body2" : "subtitle2"}
                        color="black"
                        gutterBottom
                      >
                        {SUMMARY_LABELS.period}
                      </Typography>
                      <Typography 
                        variant={responsive.isMobile ? "body1" : "body1"} 
                        fontWeight="medium"
                        sx={{ fontSize: responsive.isMobile ? "0.875rem" : "1rem" }}
                      >
                        {formatDateRange(
                          attendanceData.summary.date_range.start_date,
                          attendanceData.summary.date_range.end_date
                        )}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="black" 
                        mt={1}
                        sx={{ fontSize: responsive.isMobile ? "0.75rem" : "0.875rem" }}
                      >
                        {attendanceData.summary.message}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Summary Cards */}
                <>
                  <Grid item xs={12} sm={12} md={2.4}>
                    <Card sx={{ height: "100%", boxShadow: 2 }}>
                      <CardContent sx={{ p: responsive.isMobile ? 2 : 3 }}>
                        <Typography
                          variant={responsive.isMobile ? "body2" : "subtitle2"}
                          color="black"
                          gutterBottom
                        >
                          {SUMMARY_LABELS.totalWorkingHours}
                        </Typography>
                        <Typography 
                          variant={responsive.isMobile ? "h6" : "h5"} 
                          color="primary.main"
                        >
                          {attendanceData.summary.total_working_hours?.toFixed(2) || '0.00'}h
                        </Typography>
                        <Typography
                          variant="body2"
                          color="black"
                          mt={0.5}
                          sx={{ fontSize: responsive.isMobile ? "0.75rem" : "0.875rem" }}
                        >
                          {SUMMARY_LABELS.acrossRecords} {attendanceData.summary.total_records}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={12} md={2.4}>
                    <Card sx={{ height: "100%", boxShadow: 2 }}>
                      <CardContent sx={{ p: responsive.isMobile ? 2 : 3 }}>
                        <Typography
                          variant={responsive.isMobile ? "body2" : "subtitle2"}
                          color="black"
                          gutterBottom
                        >
                          {SUMMARY_LABELS.wfhDays}
                        </Typography>
                        <Typography variant={responsive.isMobile ? "h6" : "h5"} color="info.main">
                          {attendanceData.summary.wfh_count || 0}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="black"
                          mt={0.5}
                          sx={{ fontSize: responsive.isMobile ? "0.75rem" : "0.875rem" }}
                        >
                          {SUMMARY_LABELS.workFromHome}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={12} md={2.4}>
                    <Card sx={{ height: "100%", boxShadow: 2 }}>
                      <CardContent sx={{ p: responsive.isMobile ? 2 : 3 }}>
                        <Typography
                          variant={responsive.isMobile ? "body2" : "subtitle2"}
                          color="black"
                          gutterBottom
                        >
                          {SUMMARY_LABELS.regularisedDays}
                        </Typography>
                        <Typography variant={responsive.isMobile ? "h6" : "h5"} color="warning.main">
                          {attendanceData.summary.total_regularizations || 0}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="black"
                          mt={0.5}
                          sx={{ fontSize: responsive.isMobile ? "0.75rem" : "0.875rem" }}
                        >
                          {SUMMARY_LABELS.regularisedDays}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={12} md={2.4}>
                    <Card sx={{ height: "100%", boxShadow: 2 }}>
                      <CardContent sx={{ p: responsive.isMobile ? 2 : 3 }}>
                        <Typography
                          variant={responsive.isMobile ? "body2" : "subtitle2"}
                          color="black"
                          gutterBottom
                        >
                          {SUMMARY_LABELS.totalLeaves}
                        </Typography>
                        <Typography variant={responsive.isMobile ? "h6" : "h5"} color="success.main">
                          {attendanceData.summary.total_leaves || 0}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="black"
                          mt={0.5}
                          sx={{ fontSize: responsive.isMobile ? "0.75rem" : "0.875rem" }}
                        >
                          {SUMMARY_LABELS.allLeaveTypes}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </>
              </Grid>
            )}

            

            {/* Help Section */}
            {!attendanceData?.results?.length && (
              <Box sx={{ mb: 3 }}>
                <Card sx={{ backgroundColor: "#f8f9fa", border: "1px solid #e9ecef" }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Employee View:</strong> Showing attendance records in a consistent format for all users
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* Data Table Section */}
            <Box>
              {!attendanceData || attendanceData.results.length === 0 ? (
                <Box
                  sx={{
                    mt: 12,
                    left: "50%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <NothingToDisplayComponent />
                </Box>
              ) : (
                <Paper
                  elevation={2}
                  sx={{ borderRadius: 2, overflow: "hidden", margin: "0 auto" }}
                >
                  <DataGrid
                    {...getOptimizedDataGridProps(responsive)}
                    rows={rows}
                    columns={columns}
                    slots={{
                      // toolbar: responsive.isDesktop ? GridToolbar : undefined,  // Commented out COLUMNS, FILTERS, DENSITY buttons
                      noRowsOverlay: CustomNoRowsOverlay,
                    }}
                    slotProps={{
                      // toolbar: {  // Commented out toolbar props
                      //   showQuickFilter: true,
                      //   quickFilterProps: { debounceMs: 500 },
                      // },
                    }}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: responsive.getPageSize(),
                        },
                      },
                    }}
                    pageSizeOptions={responsive.isMobile 
                      ? [5, 10] 
                      : responsive.isTablet 
                      ? [10, 15, 25] 
                      : [10, 25, 50, 100]
                    }
                  />
                </Paper>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </ErrorBoundary>
  );
};

export default EmployeeAttendance;
